import { useState, useEffect, useRef, useContext } from "react";
import { Form, Button, Spinner, Row, Col } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { UserInfo } from "../helper/Context.js";
import * as Yup from "yup";
import { Formik } from "formik";
import { APIContact } from "../helper/APIFunctions.js";
import "./ContactPage.css";
import "./NewHomePage.css";

const validationSchema = Yup.object().shape({
  email: Yup.string().required("Required").email("Invalid Format"),
  comment: Yup.string().required("Required"),
  header: Yup.string().required("Required"),
});

const ContactPage = (props) => {
  const canvasRef = useRef(null);
  const { userInfo } = useContext(UserInfo);
  const [status, setStatus] = useState({});
  const location = useLocation();

  useEffect(() => {
    // This hook specifically handles scrolling to the anchor when the URL changes.
    // A timeout ensures that it runs after the browser's own attempts and
    // after other content (like the canvas) has had time to render.
    if (location.hash) {
      const id = location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [location]); // Reruns whenever the URL changes

  const getInitialSubject = () => {
    const params = new URLSearchParams(location.search);
    return params.get('subject') || '';
  };

  useEffect(() => {
    // This hook is now only for the canvas animation.
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let stars = [];
    const numStars = 250;
    let animationFrameId;

    const handleResize = () => {
      const w = window.innerWidth;
      const h = document.body.scrollHeight;
      canvas.width = w;
      canvas.height = h;
      stars = [];
      for (let i = 0; i < numStars; i++) {
        stars.push({
          x: Math.random() * w,
          y: Math.random() * h,
          size: Math.random() * 1.5 + 0.5,
          speed: Math.random() * 0.4 + 0.1,
        });
      }
    };

    function draw() {
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "rgba(255, 255, 255, 0.8)";

      stars.forEach(star => {
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fill();
        star.y -= star.speed;
        if (star.y < 0) {
          star.y = canvas.height;
          star.x = Math.random() * canvas.width;
        }
      });

      animationFrameId = requestAnimationFrame(draw);
    }

    handleResize();
    draw();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return (
    <div
      className="homepage-container"
      style={{
        width: '100%',
        position: 'relative',
        overflowX: 'hidden',
        background: 'linear-gradient(to bottom, #0a0f2c, #1b1f3a, #2d304d)',
      }}
    >
      <canvas
        id="stars"
        ref={canvasRef}
        style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}
      ></canvas>
      <div
        className="overlay"
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'radial-gradient(circle at center, rgba(0,0,0,0.1), rgba(0,0,0,0.98))',
          pointerEvents: 'none',
          zIndex: 1
        }}
      ></div>

      <div id="contact-form-top" className="main-container-narrower contact-page-form" style={{ position: 'relative', zIndex: 2, background: 'transparent' }}>
        <h2 className="TitleText mb-4">
          {status.success ? "We'll get back to you!" : "How can we help you?"}
        </h2>
        {status.error ? <p className="text-danger">{status.error}</p> : null}
        {status.success ? (
          <p>
            <center>       <h4> We look forward to helping you out. We typically respond within 24 hours.</h4></center>
          </p>
        ) : (
          <Formik
            initialValues={{
              email: (userInfo || {}).email || "",
              comment: "",
              header: getInitialSubject(),
            }}
            validationSchema={validationSchema}
            onSubmit={(values, actions) => {
              APIContact(
                values,
                () => {
                  actions.setSubmitting(false);
                  setStatus({ success: true });
                },
                (err) => {
                  actions.setSubmitting(false);
                  setStatus({ error: err });
                }
              );
            }}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              touched,
              errors,
              isSubmitting,
            }) => {
              return (
                <Form noValidate onSubmit={handleSubmit}>
                  <h4>We'd love to hear from you! We typically respond within 24 hours.</h4>
                  <Form.Group className="my-3">
                    <Form.Label>Subject</Form.Label>
                    <Form.Control
                      name="header"
                      placeholder="Message subject"
                      autoComplete="off"
                      value={values.header}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      isInvalid={touched.header && errors.header}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.header}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className={"my-3"}>
                    <Form.Label>Your contact email</Form.Label>
                    <Form.Control
                      placeholder="Your email"
                      type="email"
                      name="email"
                      autoComplete="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      isInvalid={touched.email && errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="my-3">
                    <Form.Label>Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="comment"
                      value={values.comment}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      isInvalid={touched.comment && errors.comment}
                      rows={4}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.comment}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Button
                    type="submit"
                    className="mt-2"
                    disabled={isSubmitting || status.success}
                  >
                    {isSubmitting ? (
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="login"
                        aria-hidden="true"
                      />
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </Form>
              );
            }}
          </Formik>
        )}
      </div>
    </div>
  );
};

export default ContactPage;
